import { useDataProvider, useRedirect, useResourceContext } from 'react-admin';
import { Toolbar, SaveButton, DeleteButton, AutocompleteArrayInput, useGetList } from 'react-admin';
import { ToolbarDuplicateButton } from '../buttons/toolbar-duplicate-button';
import { useFormState, useWatch } from 'react-hook-form';
import { useRecordContext } from 'react-admin';
import { useState, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { useQuery } from '@apollo/client';
import {
  TargetListsForDropdownDocument,
  TargetListsOrderBy,
  TargetListFilter,
} from 'src/generated/client';
import { resolveCreativeParentLineItemShowPath } from 'src/client/utility/pio-native-lineitem-fk-reads';

export const CreativeToolbar = () => {
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const resource = useResourceContext();
  const { isValid, isDirty } = useFormState();
  const disableSave = !isDirty && !isValid;
  const record = useRecordContext();

  // The legacy /line_items show page can no longer load (PIO API shut off), so
  // after deleting a creative land on the native /lineItems show page,
  // translating a legacy-only id via externalPioId.
  const onSuccess = async () => {
    const path = await resolveCreativeParentLineItemShowPath(dataProvider, {
      placementsLineItemId: record?.placementsLineItemId,
      lineItemId: record?.lineItemId,
    });
    redirect(path ?? 'list', resource);
  };

  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <SaveButton disabled={disableSave} />
      <ToolbarDuplicateButton />
      <DeleteButton mutationMode="pessimistic" mutationOptions={{ onSuccess }} />
    </Toolbar>
  );
};

interface SelectInputChoice {
  id: string | number;
  name: string;
}

type AutocompleteInputProps = {
  resource: string;
  source: string;
  validate?: (value: any, allValues: any) => string | undefined;
};

export const AutocompleteInput = ({resource, source, validate}: AutocompleteInputProps) => {
  const [choices, setChoices] = useState<SelectInputChoice[]>([]);
  const label = resource.charAt(0).toUpperCase() + resource.slice(1)

  const result = useGetList(resource, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  });

  useEffect(() => {
    if (result.isLoading) return;
    setChoices(result.data as SelectInputChoice[]);
  }, [result]);

  return (
    <Box display="flex">
      <Box>
        <AutocompleteArrayInput
          label={label}
          source={source}
          choices={choices}
          sx={{ minWidth: 250 }}
          validate={validate ?? undefined}
        />
      </Box>
    </Box>
  );
};

type TargetListAutocompleteInputProps = {
  source: string;
  validate?: (value: any, allValues: any) => string | undefined;
};

export const TargetListAutocompleteInput = ({source, validate}: TargetListAutocompleteInputProps) => {
  const [filterValue, setFilterValue] = useState('');
  const record = useRecordContext();

  // Track currently selected values to ensure they stay in choices
  const watchedIds = useWatch({ name: source });
  const selectedIds = useMemo(() => watchedIds || [], [watchedIds]);

  // Keep a cache of selected items so they persist in choices even when not in query results
  const [selectedItemsCache, setSelectedItemsCache] = useState<Map<string | number, SelectInputChoice>>(new Map());

  // IDs from the record that need to be fetched (not already in cache)
  const [idsToFetch, setIdsToFetch] = useState<number[]>([]);

  // Pre-populate cache with existing target lists from the record (for edit mode)
  // Handle both targetListsList (simple array) and targetLists.nodes (connection format)
  useEffect(() => {
    const existingTargetLists = record?.targetListsList || record?.targetLists?.nodes || [];
    if (existingTargetLists.length > 0) {
      setSelectedItemsCache(prev => {
        const newCache = new Map(prev);
        existingTargetLists.forEach((tl: { id: string | number; name: string }) => {
          newCache.set(tl.id, { id: tl.id, name: tl.name });
        });
        return newCache;
      });
    }

    // If we only have IDs (targetListIdsList) but no names, we need to fetch them
    const targetListIds = record?.targetListIdsList || [];
    if (targetListIds.length > 0 && existingTargetLists.length === 0) {
      setIdsToFetch(targetListIds.filter((id: number) => id !== null && id !== undefined));
    }
  }, [record?.targetListsList, record?.targetLists?.nodes, record?.targetListIdsList]);

  // Fetch target lists by ID when we have IDs but no names
  const { data: fetchedByIdData } = useQuery(TargetListsForDropdownDocument, {
    variables: {
      first: 100,
      offset: 0,
      orderBy: [TargetListsOrderBy.NameAsc],
      filter: { id: { in: idsToFetch } },
    },
    skip: idsToFetch.length === 0,
  });

  // Add fetched target lists to cache
  useEffect(() => {
    const fetchedNodes = fetchedByIdData?.targetLists?.nodes ?? [];
    if (fetchedNodes.length > 0) {
      setSelectedItemsCache(prev => {
        const newCache = new Map(prev);
        fetchedNodes.forEach((tl: SelectInputChoice) => {
          newCache.set(tl.id, { id: tl.id, name: tl.name });
        });
        return newCache;
      });
      setIdsToFetch([]); // Clear after fetching
    }
  }, [fetchedByIdData?.targetLists?.nodes]);

  // Build the filter for GraphQL query
  // Always exclude archived target lists; when searching, filter by name or ID
  const filter = useMemo((): TargetListFilter => {
    const baseFilter: TargetListFilter = { isArchived: { equalTo: false } };

    if (filterValue.length >= 2) {
      // Check if the search value is a valid number (for ID search)
      const parsedId = parseInt(filterValue, 10);
      const isValidId = !isNaN(parsedId) && parsedId.toString() === filterValue.trim();

      // If it's a valid number, search by both ID and name using OR
      if (isValidId) {
        return {
          ...baseFilter,
          or: [
            { id: { equalTo: parsedId } },
            { name: { includesInsensitive: filterValue } }
          ]
        };
      }

      // Otherwise, just search by name
      return { ...baseFilter, name: { includesInsensitive: filterValue } };
    }
    return baseFilter;
  }, [filterValue]);

  // Build sort order - when not searching, sort by uploadedAt (most recent first)
  // When searching, sort by name for better search results
  const orderBy = useMemo((): TargetListsOrderBy[] => {
    if (filterValue.length < 2) {
      return [TargetListsOrderBy.UploadedAtDesc];
    }
    return [TargetListsOrderBy.NameAsc];
  }, [filterValue]);

  // Use Apollo query directly with lightweight query (only id and name)
  // Uses previousData to prevent UI flicker during filter changes
  const { data, loading, previousData } = useQuery(TargetListsForDropdownDocument, {
    variables: {
      first: 100,
      offset: 0,
      orderBy,
      filter,
    },
  });

  // Use previous data while loading to prevent flicker when filter changes
  const currentData = loading ? previousData : data;

  // Update cache when we get new data that includes selected items
  useEffect(() => {
    const nodes = currentData?.targetLists?.nodes ?? [];
    if (nodes.length > 0) {
      setSelectedItemsCache(prev => {
        const newCache = new Map(prev);
        nodes.forEach((node: SelectInputChoice) => {
          // Cache any item that's currently selected or that we fetch
          if (selectedIds.includes(node.id)) {
            newCache.set(node.id, { id: node.id, name: node.name });
          }
        });
        return newCache;
      });
    }
  }, [currentData?.targetLists?.nodes, selectedIds]);

  // Memoize choices, ensuring selected items are always included
  const choices = useMemo(() => {
    const nodes = currentData?.targetLists?.nodes ?? [];
    const nodeIds = new Set(nodes.map((n: SelectInputChoice) => n.id));

    // Start with query results
    let result = [...nodes];

    // Add any selected items that aren't in the current query results
    selectedIds.forEach((id: string | number) => {
      if (!nodeIds.has(id)) {
        const cached = selectedItemsCache.get(id);
        if (cached) {
          result.push(cached);
        }
      }
    });

    // When showing recent items (not searching), sort alphabetically for display.
    // Server returns 100 most recent (by uploadedAt), we then sort by name for UX.
    if (filterValue.length < 2) {
      return result.sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }
    // When searching, already sorted by name from server
    return result;
  }, [currentData?.targetLists?.nodes, filterValue, selectedIds, selectedItemsCache]);

  return (
    <Box display="flex">
      <Box>
        <AutocompleteArrayInput
          label="Target Lists"
          source={source}
          choices={choices as SelectInputChoice[]}
          sx={{ minWidth: 250 }}
          validate={validate ?? undefined}
          // Debounce user input to avoid too many requests
          debounce={300}
          // Let users type to filter - just return the search text
          filterToQuery={searchText => searchText}
          // Update local filter state which triggers new query
          setFilter={setFilterValue}
          // Always show suggestions to allow browsing first 100 or searching
          shouldRenderSuggestions={() => true}
          noOptionsText="No target lists found"
          isLoading={loading}
          // Compare by id to handle objects with different shapes (e.g., with/without __typename)
          isOptionEqualToValue={(option, value) => option.id === value.id}
          // Helper text to guide users
          helperText={filterValue.length === 0
            ? "Showing 100 most recently created target lists. Type to search by name or ID."
            : filterValue.length === 1
            ? "Type at least 2 characters to search"
            : undefined
          }
        />
      </Box>
    </Box>
  );
};
